import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  Row,
  Col,
  Button,
  FormLabel,
  FormControl,
  FormGroup,
  Card,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import DataSetType from '../../../../models/audience/DataSetType';
import InputTableDataSet from '../../../../models/audience/InputTableDataSet';
import Store from '../../../../store';
import { getDatasetParameter } from '../../../../utils';
import { MAX_NUMBER_OF_LOADING_DATASETS } from '../../../../utils/constants';
import Spinner from '../../../components/Spinner';
import Error from '../../../components/error/Error';

interface DatabaseImportPageProps extends RouteComponentProps {
  store?: Store;
  /**
   * Where this is mounted. `page` is the route, unchanged. `modal` drops the way back,
   * the page heading and the centred width. Mirrors `MkAudiencePage` and `AudiencePage`.
   */
  chrome?: 'page' | 'modal';
  /** Called instead of navigating, once the import has been accepted. */
  onDone?: () => void;
  /** Called when the reader backs out. Only used when `chrome` is `modal`. */
  onCancel?: () => void;
}

interface DatabaseImportPagePageState {
  loading: boolean;
  currentLoadingModels: number[];
}

const DatabaseImportPage = inject('store')(
  observer(
    class DatabaseImportPage extends React.Component<
      DatabaseImportPageProps,
      DatabaseImportPagePageState
    > {
      errorMessageRef: React.RefObject<HTMLDivElement>;

      constructor(props: DatabaseImportPageProps) {
        super(props);

        this.state = {
          loading: true,
          currentLoadingModels: [],
        };

        makeObservable(this, {
          handleDataSetChange: action,
        });

        this.handleDataSetChange = this.handleDataSetChange.bind(this);
        this.uploadDataSetsTables = this.uploadDataSetsTables.bind(this);

        this.errorMessageRef = React.createRef();
      }

      async componentDidMount() {
        const { tenant } = this.props.store;
        const currentLoadingModels =
          await this.props.store.mkAudiencePage.getCurrentLoadingModels(tenant);

        this.setState({ loading: false, currentLoadingModels });
      }

      componentDidUpdate(): void {
        const {
          csvPage: { dataSets },
        } = this.props.store;

        if (dataSets.uploadDataSetError) {
          this.errorMessageRef.current.scrollIntoView();
        }
      }

      async uploadDataSetsTables() {
        const dataset: DataSetType = getDatasetParameter(
          this.props.location.search
        );
        await this.props.store.uploadDataSetsFromRedshift(dataset);
        const { tenant, modelId, csvPage } = this.props.store;
        if (csvPage.dataSets.uploadDataSetError) return;
        if (this.props.onDone) {
          this.props.onDone();
          return;
        }
        // `models`, plural. This was the one place in the app writing `/model/`, so a
        // successful Redshift import landed on a path no route matches.
        this.props.history.push(`/tenant/${tenant}/models/${modelId}/overview`);
      }

      handleDataSetChange(value: string, type: string) {
        this.props.store.csvPage.dataSets.setTableValue(type, value);
      }

      render() {
        const { csvPage, isAllowedToEdit, tenant, modelId, rootPage } =
          this.props.store;
        const { loading, currentLoadingModels } = this.state;
        const { dataSets } = csvPage;
        const { isDataSetLoadingProgress } = rootPage;
        const dataset: DataSetType = getDatasetParameter(
          this.props.location.search
        );
        const inModal = this.props.chrome === 'modal';
        // Words are the one difference a stylesheet cannot carry. Every call reads
        // "dialog wording, then exactly what the route said before".
        const t = (dialog: React.ReactNode, page: React.ReactNode) =>
          inModal ? dialog : page;
        const tableDataset = dataSets.inputTableDataSets.find(
          (t) => t.type === (dataset || 'training')
        ).table;
        const dataSetsValid = tableDataset && tableDataset.trim().length > 0;

        // Full width under its own label, like the file input and the selects. It was a
        // bold label beside a control three columns wide, which is a quarter of the card
        // to type a table name into.
        const tableControl = (
          field: InputTableDataSet,
          placeholder?: string
        ) => (
          <FormControl
            type="text"
            placeholder={placeholder}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              this.handleDataSetChange(event.target.value, field.type)
            }
          />
        );

        const getFieldsInput = (field: InputTableDataSet) =>
          t(
            <FormGroup controlId={`field_table_${field.type}`}>
              <FormLabel className="dsf-label">Redshift table name</FormLabel>
              {tableControl(field, 'schema.table')}
            </FormGroup>,
            <Row>
              <FormGroup controlId={`field_table_${field.type}`}>
                <Row>
                  <Col md="auto" className="my-auto">
                    <FormLabel className="fw-bold">
                      Redshift table name
                    </FormLabel>
                  </Col>
                  <Col md={3}>{tableControl(field)}</Col>
                </Row>
              </FormGroup>
            </Row>
          );

        const cantLoad =
          currentLoadingModels.length >= MAX_NUMBER_OF_LOADING_DATASETS;

        if (loading) {
          return <Spinner />;
        }

        return (
          <>
            {!inModal && (
              <LinkContainer
                to={`/tenant/${tenant}/models/${modelId}/overview`}
                activeClassName=""
              >
                <Button variant="link" className="me-2">
                  Go back to Overview
                </Button>
              </LinkContainer>
            )}
            <div
              // `dsf` only in the dialog. Every rule in datasetForm.css sits under
              // that class, so on the route, which is mounted for every tenant with
              // no flag, none of them match and the page renders as it did.
              className={
                inModal ? 'dsf d-grid gap-4' : 'w-75 mt-4 mx-auto d-grid gap-4'
              }
            >
              {dataSets.uploadDataSetError && (
                <div ref={this.errorMessageRef}>
                  <Error
                    message={dataSets.uploadDataSetError.message}
                    type={dataSets.uploadDataSetError.type}
                  />
                </div>
              )}
              {!inModal && <h4>Edit model datasets</h4>}
              <div className="dsf-pair">
                <Card border="light">
                  <Card.Body>
                    <Card.Title>
                      {dataset
                        ? String(dataset).charAt(0).toUpperCase() +
                          dataset.slice(1)
                        : 'Training'}
                    </Card.Title>
                    <p className="mb-4">
                      Contacts who converted and who did not. The segmentation
                      is built from these.
                    </p>
                    {t(
                      <>
                        {t(
                          <>
                            <div className="dsf-cols">
                              <span className="dsf-cols-label">
                                Required columns
                              </span>
                              <code className="dsf-col">email</code>
                              <code className="dsf-col">target</code>
                            </div>
                            <p className="dsf-cols-note">
                              <b>target</b> is 1 when the record converted and 0
                              when it did not. For a list of domains, prefix
                              each one with
                              <b> madkudu@</b>.
                            </p>
                          </>,
                          <>
                            <p>
                              The <b>validation dataset</b> must contain the
                              following columns
                            </p>
                            <ul>
                              <li>
                                email: contains the email of the record. If
                                using a list of domains append
                                &ldquo;madkudu@&rdquo; to the domains.
                              </li>
                              <li>
                                target: value of 1 if the record has converted,
                                0 otherwise
                              </li>
                              <li>
                                amount: if the record has converted, value of
                                the opportunity
                              </li>
                              <li>
                                target_closed_won: Values 1 or 0 to identify if
                                the records converted into a Closed Won
                                opportunity
                              </li>
                              <li>
                                amount_closed_won: if converted into a Closed
                                Won opportunity, the amount value of the deal
                              </li>
                            </ul>
                            <p>
                              This will allow evaluating the impact of the model
                              on the revenue pipeline
                            </p>
                          </>
                        )}
                      </>,
                      <>
                        <p>
                          The <b>training dataset</b> must contain two columns:
                          &lsquo;email&rsquo; and &lsquo;target&rsquo;.
                        </p>
                        <ul>
                          <li>
                            email: contains the email of the record. If using a
                            list of domains append &ldquo;madkudu@&rdquo; to the
                            domains.
                          </li>
                          <li>
                            target: value of 1 if the record has converted, 0
                            otherwise
                          </li>
                        </ul>
                      </>
                    )}
                    {getFieldsInput(
                      dataSets.inputTableDataSets.find(
                        (t) => t.type === (dataset || 'training')
                      )
                    )}
                  </Card.Body>
                </Card>

                {!dataset && (
                  <Card border="light">
                    <Card.Body>
                      <Card.Title>
                        {t('Validation', 'Validation dataset')}
                      </Card.Title>
                      <p className="mb-4">
                        A different period, to check the segmentation holds
                        outside what it was built on.
                      </p>
                      <div className="dsf-cols">
                        <span className="dsf-cols-label">Required columns</span>
                        <code className="dsf-col">email</code>
                        <code className="dsf-col">target</code>
                        <code className="dsf-col">amount</code>
                        <code className="dsf-col">target_closed_won</code>
                        <code className="dsf-col">amount_closed_won</code>
                      </div>
                      <p className="dsf-cols-note">
                        The amounts are what let the model be measured against
                        pipeline rather than against record counts:
                        <b> amount</b> for a converted record,
                        <b> amount_closed_won</b> for one that closed.
                      </p>
                      {getFieldsInput(
                        dataSets.inputTableDataSets.find(
                          (t) => t.type === 'validation'
                        )
                      )}
                    </Card.Body>
                  </Card>
                )}
              </div>

              <div className={inModal ? 'mb-2' : 'mx-auto mb-4'}>
                {inModal ? (
                  <Button
                    variant="outline-primary"
                    className="me-2"
                    onClick={this.props.onCancel}
                  >
                    Cancel
                  </Button>
                ) : (
                  <LinkContainer
                    to={`/tenant/${tenant}/models/${modelId}/import_data`}
                    activeClassName=""
                  >
                    <Button variant="outline-primary" className="me-2">
                      Cancel
                    </Button>
                  </LinkContainer>
                )}

                <OverlayTrigger
                  show={cantLoad}
                  overlay={
                    <Tooltip>
                      {`Model ${currentLoadingModels[0]} and ${currentLoadingModels[1]} are currently loading. Please wait until they both load to begin loading another model`}
                    </Tooltip>
                  }
                >
                  <span>
                    <Button
                      variant="primary"
                      disabled={
                        !dataSetsValid ||
                        isDataSetLoadingProgress ||
                        !isAllowedToEdit ||
                        cantLoad
                      }
                      onClick={this.uploadDataSetsTables}
                      className="pull-right"
                    >
                      {isDataSetLoadingProgress
                        ? `Uploading dataset${!dataset ? 's' : ''}..`
                        : `Upload dataset${!dataset ? 's' : ''}`}
                    </Button>
                  </span>
                </OverlayTrigger>
              </div>
            </div>
          </>
        );
      }
    }
  )
);

export default withRouter(DatabaseImportPage);
